import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  IconButton,
  Typography,
  Paper,
  Chip
} from '@mui/material';
import { Alert } from '@mui/material';
import { CheckCircle, Error as ErrorIcon, FolderOpen, HourglassEmpty } from '@mui/icons-material';
import { offerStandaloneManageService } from '../../services/offerStandaloneManageService';

type BulkItem = {
  package_symbol: string;
  name: string;
  stage?: string;
  package_type?: string;
  description?: string;
  parameters?: string;
  estimated_value?: number;
};

type RowState = {
  data: BulkItem;
  files: File[];
  status: 'pending' | 'processing' | 'done' | 'error';
  error?: string;
  summary?: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
  offerId: string;
  initialPackages: BulkItem[];
  onCompleted?: (summary: { successes: number; failures: number; total: number }) => void;
}

async function collectFilesFromDirectoryHandle(dirHandle: any): Promise<File[]> {
  const out: File[] = [];
  // @ts-ignore
  for await (const entry of dirHandle.values ? dirHandle.values() : dirHandle.entries()) {
    const handle = entry.kind ? entry : entry[1];
    if (handle.kind === 'file') {
      const file = await handle.getFile();
      out.push(file);
    } else if (handle.kind === 'directory') {
      const nested = await collectFilesFromDirectoryHandle(handle);
      out.push(...nested);
    }
  }
  return out;
}

const OfferBulkImportPackagesDialog: React.FC<Props> = ({ open, onClose, offerId, initialPackages, onCompleted }) => {
  const [rows, setRows] = useState<RowState[]>(() =>
    (initialPackages || []).map((p) => ({ data: p, files: [], status: 'pending' }))
  );
  const [running, setRunning] = useState(false);
  const [successes, setSuccesses] = useState(0);
  const [failures, setFailures] = useState(0);
  const dirInputRef = useRef<HTMLInputElement>(null);
  const [dirPickIndex, setDirPickIndex] = useState<number | null>(null);

  const totalDone = useMemo(() => rows.filter((r) => r.status === 'done').length, [rows]);

  useEffect(() => {
    if (open) {
      setRows((initialPackages || []).map((p) => ({ data: p, files: [], status: 'pending' })));
      setSuccesses(0);
      setFailures(0);
    }
  }, [open, initialPackages]);

  const handlePickFolder = async (index: number) => {
    try {
      // @ts-ignore
      if (window.showDirectoryPicker) {
        // @ts-ignore
        const handle = await window.showDirectoryPicker();
        const files = await collectFilesFromDirectoryHandle(handle);
        applyPickedFiles(index, files);
        return;
      }
    } catch (e) {
      // fallback
    }
    setDirPickIndex(index);
    dirInputRef.current?.click();
  };

  const applyPickedFiles = (index: number, picked: File[]) => {
    const matched = picked;
    const totalBytes = matched.reduce((acc, f) => acc + (f.size || 0), 0);
    const totalMB = totalBytes / (1024 * 1024);
    const summary = `Wybrano ${matched.length} • ${totalMB.toFixed(1)} MB`;
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, files: matched, summary } : r)));
  };

  const handleDirInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (dirPickIndex === null) return;
    const fl = e.target.files ? Array.from(e.target.files) : [];
    applyPickedFiles(dirPickIndex, fl);
    setDirPickIndex(null);
  };

  const runImport = async () => {
    setRunning(true);
    setSuccesses(0);
    setFailures(0);
    try {
      for (let i = 0; i < rows.length; i++) {
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: 'processing', error: undefined } : r)));
        const row = rows[i];
        try {
          const payload: any = {
            package_symbol: row.data.package_symbol,
            name: row.data.name,
            package_type: row.data.package_type || undefined,
            description: row.data.description || undefined,
            parameters: row.data.parameters || undefined,
            estimated_value: typeof row.data.estimated_value === 'number' ? row.data.estimated_value : undefined,
            __source: 'AI', // Oznaczamy jako generowane przez AI, ponieważ pochodzą z importu zbiorczego/AI
          };
          const files = row.files && row.files.length > 0 ? row.files : undefined;
          await offerStandaloneManageService.createPackage(offerId, payload, files);
          setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: 'done' } : r)));
          setSuccesses((s) => s + 1);
        } catch (e: any) {
          setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: 'error', error: e?.message || 'Błąd' } : r)));
          setFailures((f) => f + 1);
        }
      }
      const summary = { successes: successes, failures: failures, total: rows.length };
      onCompleted && onCompleted(summary);
    } finally {
      setRunning(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={running ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import pakietów z JSON ({rows.length})</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Wskaż folder z plikami dla każdego pakietu (opcjonalnie). Zapis uruchomi tworzenie po kolei.
        </Typography>
        {running && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="info" sx={{ mb: 1 }}>Trwa import pakietów… Proszę nie zamykać tego okna.</Alert>
            <LinearProgress />
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>{`Postęp: ${successes + failures}/${rows.length} · Sukcesy: ${successes} · Błędy: ${failures}`}</Typography>
          </Box>
        )}
        <Paper variant="outlined" sx={{ maxHeight: 420, overflow: 'auto' }}>
          <List dense>
            {rows.map((row, idx) => (
              <ListItem key={idx} divider>
                <ListItemText
                  primary={`${row.data.package_symbol} — ${row.data.name}`}
                  secondary={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {`${row.data.stage || ''}${row.data.package_type ? ` · ${row.data.package_type}` : ''}`}
                      </Typography>
                      {(row.data.description || row.data.parameters) && (
                        <Typography variant="caption" color="text.secondary">
                          {row.data.description || row.data.parameters}
                        </Typography>
                      )}
                      {typeof row.data.estimated_value === 'number' && (
                        <Typography variant="caption" color="text.secondary">
                          {`Szac. wartość: ${new Intl.NumberFormat('pl-PL', { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(row.data.estimated_value)} PLN`}
                        </Typography>
                      )}
                      {row.summary && (
                        <Box sx={{ mt: 0.5 }}>
                          <Chip size="small" color="success" variant="outlined" label={row.summary} sx={{ height: 20, fontSize: '0.70rem', width: 'fit-content' }} />
                        </Box>
                      )}
                    </Box>
                  }
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                  {row.status === 'done' && <CheckCircle color="success" fontSize="small" />}
                  {row.status === 'processing' && <HourglassEmpty color="primary" fontSize="small" />}
                  {row.status === 'error' && <ErrorIcon color="error" fontSize="small" />}
                </Box>
                <ListItemSecondaryAction>
                  <IconButton onClick={() => handlePickFolder(idx)} edge="end" size="small">
                    <FolderOpen />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
        <DialogActions>
          <Button onClick={onClose} disabled={running}>
            Anuluj
          </Button>
          <Button onClick={runImport} variant="contained" disabled={running}>
            {running ? <HourglassEmpty /> : <CheckCircle />} Importuj
          </Button>
        </DialogActions>
      </DialogContent>

      {/* Hidden input for fallback directory picking */}
      <input type="file" multiple ref={dirInputRef} style={{ display: 'none' }} onChange={handleDirInputChange} />
    </Dialog>
  );
};

export default OfferBulkImportPackagesDialog;


